///
/// EarthBrowser
///
/// Created by Matt Giger on 10/30/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One instanced draw for every ember dome marker on the globe. Each
/// EmberDomeNode used to own a renderer over the shared sphere mesh, which
/// made a few thousand markers a few thousand draw calls (plus a vertex
/// array bind, two matrix uploads and a handful of uniforms each) and a
/// couple of million triangles for dots a few pixels across. Now the dome
/// nodes append their placement and look into this batch's instance array
/// as they are traversed, and the batch draws them all as camera-facing
/// impostor quads (ember_dome_instanced.glsl) at the end of the pass.
///
/// The array is rebuilt from the traversal every frame: a dome that isn't
/// visited (hidden, or under a hidden or removed parent) simply isn't in
/// it, so there are no slots to allocate or release. The upload happens
/// when the batch's draw executes, after the whole scene has been
/// collected, so traversal order between the batch and the domes doesn't
/// matter. One batch per GL context, shared through sharedEmberDomeBatch()
/// and added to the world by EarthBrowser.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type time } from "@stovokor/math/time"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Floats per instance: center.xyz + radius, color rgba, opacity/pulse/
// fill/farFill, minAngular
const cFloatsPerInstance = 13
const cInitialCapacity = 512
// Bounds large enough that the batch is never frustum culled: its
// instances can be anywhere around the globe or in orbit
const cBoundsKm = 1e7
// Depth buckets for the back-to-front ordering of the instances. The
// transparent pass blends in draw order, so overlapping domes should draw
// far to near as they did when each was its own sorted draw; a counting
// sort into this many buckets over the frame's depth range is O(n) and
// resolves order to a fraction of a percent of that range
const cDepthBuckets = 1024

/** Per-dome look, read by the batch when the dome is traversed */
export interface EmberDomeAppearance {
	color: color
	opacity: number
	pulse: number
	fill: number
	farFill: number
	minAngular: number
}

class EmberDomeBatchGeometry implements Geometry {
	public corner: VertexAttribute
	public instanceCenterRadius: VertexAttribute
	public instanceColor: VertexAttribute
	public instanceParams: VertexAttribute
	public instanceMinAngular: VertexAttribute
	public vertexBuffer: VertexBuffer
	public instanceBuffer: VertexBuffer
	public indexBuffer: IndexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds = new cube(-cBoundsKm, cBoundsKm)
	public boundingSphere = new sphere(cBoundsKm)
	public available: boolean = true
	public instanceCount: number = 0

	constructor(context: StovokorContext) {
		this.corner = new VertexAttribute(context, 2, [-1, -1, 1, -1, 1, 1, -1, 1])
		this.vertexBuffer = new VertexBuffer(context, [this.corner])
		this.indexBuffer = new IndexBuffer(context)
		this.indexBuffer.quad(0, 1, 2, 3)

		this.instanceCenterRadius = new VertexAttribute(context, 4)
		this.instanceColor = new VertexAttribute(context, 4)
		this.instanceParams = new VertexAttribute(context, 4)
		this.instanceMinAngular = new VertexAttribute(context, 1)
		const instanced = [this.instanceCenterRadius, this.instanceColor, this.instanceParams, this.instanceMinAngular]
		for(const attribute of instanced) {
			attribute.divisor = 1
		}
		this.instanceBuffer = new VertexBuffer(context, instanced)
		this.instanceBuffer.usage = WebGL2RenderingContext.DYNAMIC_DRAW
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.instanceBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

class EmberDomeBatchRenderer extends Renderer {
	public modelView = new UniformDynamic<matrix>()
	public projection = new UniformDynamic<matrix>()
	public time = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry, finalize: () => void) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("ember_dome_instanced"), geometry, [transparentMaterial])
		// The quads are view-aligned; orientation is irrelevant
		this.drawState.cullFace = false
		// Runs when the draw executes, after the frame's traversal has
		// appended every visible dome: sizes the instanced draw and queues
		// the upload the vertex array bind flushes right after
		this.preActions = [finalize]
	}

	protected stateUpdate(state: RenderState): void {
		this.modelView.value = state.modelView
		this.projection.value = state.projection
		// Seconds within the day, wrapped to keep float32 precision in the shader
		this.time.value = (state.realTime.julian % 1) * 86400
	}
}

export class EmberDomeBatch extends NodeBase {
	private readonly geometry: EmberDomeBatchGeometry
	private data = new Float32Array(cFloatsPerInstance * cInitialCapacity)
	// Instances reordered far to near for upload, and the scratch the sort uses
	private sorted = new Float32Array(cFloatsPerInstance * cInitialCapacity)
	private depths = new Float32Array(cInitialCapacity)
	private order = new Uint32Array(cInitialCapacity)
	private readonly bucketCounts = new Uint32Array(cDepthBuckets + 1)
	private count = 0
	// The frame the instance array is being filled for: the shared
	// per-tick time object the viewports hand every node
	private frame?: time
	// View matrix of the frame being filled, for the depth sort at upload
	private view?: matrix

	constructor(context: StovokorContext) {
		super()
		this.geometry = new EmberDomeBatchGeometry(context)
		this.renderer = new EmberDomeBatchRenderer(context, this.geometry, () => this.finalize())
	}

	/** Number of domes appended for the frame being drawn (debugging) */
	public get instanceCount(): number {
		return this.count
	}

	/**
	 * Append one dome. `model` is its world matrix: the origin is the
	 * center and the x axis length the radius (domes are uniformly scaled
	 * unit spheres)
	 */
	public add(state: RenderState, model: matrix, look: EmberDomeAppearance): void {
		const m = model.glArrayAccess()
		const radius = Math.sqrt(m[0] * m[0] + m[1] * m[1] + m[2] * m[2])
		this.addPoint(state, m[12], m[13], m[14], radius, look)
	}

	/**
	 * Append one dome by world-space center and radius (km), for layers
	 * that keep their markers in arrays instead of nodes (the webcam cloud)
	 */
	public addPoint(state: RenderState, x: number, y: number, z: number, radius: number, look: EmberDomeAppearance): void {
		this.beginFrame(state)
		if((this.count + 1) * cFloatsPerInstance > this.data.length) {
			const grown = new Float32Array(this.data.length * 2)
			grown.set(this.data)
			this.data = grown
			this.sorted = new Float32Array(grown.length)
			this.depths = new Float32Array(this.depths.length * 2)
			this.order = new Uint32Array(this.order.length * 2)
		}
		const c = look.color
		const d = this.data
		let i = this.count * cFloatsPerInstance
		d[i++] = x
		d[i++] = y
		d[i++] = z
		d[i++] = radius
		d[i++] = c.r
		d[i++] = c.g
		d[i++] = c.b
		d[i++] = c.a
		d[i++] = look.opacity
		d[i++] = look.pulse
		d[i++] = look.fill
		d[i++] = look.farFill
		d[i] = look.minAngular
		this.count += 1
	}

	public override render(state: RenderState): RenderActionSet[] {
		// Also marks the frame when no dome did (all hidden), so a stale
		// array isn't drawn
		this.beginFrame(state)
		return super.render(state)
	}

	private beginFrame(state: RenderState): void {
		if(this.frame !== state.realTime) {
			this.frame = state.realTime
			this.view = state.viewMatrix
			this.count = 0
		}
	}

	private finalize(): void {
		const count = this.count
		this.geometry.instanceCount = count
		if(count > 0) {
			this.sortBackToFront(count)
			// The whole capacity goes up each time so the byte length is
			// stable and the buffer takes a bufferSubData rather than a
			// reallocation; instanceCount limits what is drawn
			this.geometry.instanceBuffer.uploadData(this.sorted)
		}
	}

	/**
	 * Reorder the appended instances into `sorted`, farthest first, by the
	 * view-space depth of their centers. Stable counting sort over
	 * cDepthBuckets, so instances at the same depth keep traversal order
	 */
	private sortBackToFront(count: number): void {
		const view = this.view
		const data = this.data
		const depths = this.depths
		if(view === undefined) {
			this.sorted.set(data.subarray(0, count * cFloatsPerInstance))
			return
		}

		// Depth in front of the camera: the view matrix's z row applied
		// to the center (see RenderState.viewDepthOf)
		const v = view.glArrayAccess()
		let min = Number.POSITIVE_INFINITY
		let max = Number.NEGATIVE_INFINITY
		for(let i = 0; i < count; i++) {
			const o = i * cFloatsPerInstance
			const depth = -(v[2] * data[o] + v[6] * data[o + 1] + v[10] * data[o + 2] + v[14])
			depths[i] = depth
			if(depth < min) min = depth
			if(depth > max) max = depth
		}

		const counts = this.bucketCounts
		counts.fill(0)
		const scale = max > min ? cDepthBuckets / (max - min) : 0
		// Bucket 0 is the farthest so the prefix sums place far instances first
		const order = this.order
		for(let i = 0; i < count; i++) {
			let bucket = Math.floor((max - depths[i]) * scale)
			if(bucket >= cDepthBuckets) bucket = cDepthBuckets - 1
			order[i] = bucket
			counts[bucket + 1]++
		}
		for(let b = 1; b <= cDepthBuckets; b++) {
			counts[b] += counts[b - 1]
		}

		const sorted = this.sorted
		for(let i = 0; i < count; i++) {
			const slot = counts[order[i]]++
			const from = i * cFloatsPerInstance
			const to = slot * cFloatsPerInstance
			for(let k = 0; k < cFloatsPerInstance; k++) {
				sorted[to + k] = data[from + k]
			}
		}
	}
}

// One batch per GL context
const sharedBatches = new WeakMap<StovokorContext, EmberDomeBatch>()

export function sharedEmberDomeBatch(context: StovokorContext): EmberDomeBatch {
	let batch = sharedBatches.get(context)
	if(!batch) {
		batch = new EmberDomeBatch(context)
		sharedBatches.set(context, batch)
	}
	return batch
}
